import * as Sentry from "@sentry/react";
import { FormikProps, FormikValues } from "formik";
import UploadDniCard, {
  DniStep,
} from "../../components/UploadDniCard/UploadDniCard";
import LivenessValidationCard from "../../components/LivenessValidationCard/LivenessValidationCard";
import { hideLivenessStepHeader } from "../../components/LivenessValidationCard/utils/utils";
import {
  authAdditionalSignerEmail,
  getBusinessDocuments,
  getCountryList,
  getDraft,
  getPhysicalActivities,
  getSignatoryDraft,
  sendBusinessRepresentativeDocument,
  sendStep,
} from "../../connectors/connectors";
import {
  mediumSelectedAddressRequired,
  cuitRequired,
  radioOrSingleSelectRequired,
  smallStringRequired,
  multipleSelectRequired,
  smallString,
  zipCodeRequired,
  checkboxRequired,
  cuitPFRequired,
} from "../../validations";
import {
  Step,
  Template,
  Option,
  PitbullActivities,
  PitbullCountries,
  ComponentStep,
  PersonType,
  IdentityType,
  IdentityVersion,
  GenderInitials,
  PreconfirmationStep,
  PitbullCountry,
  PitbullActivityPhysical,
  StepFourPJ,
  Representative,
  StepSixPJ,
  StepTwoPJ,
  PreconfirmationField,
  PitbullProvinces,
  DocumentBusinessType,
  DocumentMetadata,
  Signatory,
  StepThreePJ,
  Field,
  StepOnePJ,
  SignatoryData,
  DocumentRepresentativeType,
} from "../../types";
import bodyIcon from "../../assets/images/icons/ui/body.svg";
import {
  capitalizeString,
  fuseProvince,
  getBusinessDocumentsByCorporateType,
  getDocumentTypeParsed,
  getDraftStepsToSend,
  getProvincesByIso,
  parseBirthDate,
  parseDate,
  parseGenderFromInitial,
  parseRegistrationAuthority,
  parseTelephone,
  translateCorporateReversed,
  onContinueRenaper,
  isTokenActive,
  getParsedRenaperData,
  buildFullAddress,
} from "../../app/utils";
import TokenStep from "../../components/TokenStep";
import {
  onUpdateCompletedStep,
  onUpdateDraftUUID,
  onUpdateFormData,
  onUpdateHasExternalInformation,
  onUpdateReachedStep,
  onUpdateStep,
  onUpdateSubStep,
} from "../../reducers/onboardingDataReducer";
import {
  hasRenaperData,
  asCuit,
  asDni,
  checkIsEmailAvailable,
  contactBlacklistSupport,
  deleteDniImages,
  deleteLiveness,
  genericToastifyError,
  getDniFromCuit,
  getFormattedPreconfirmationFields,
  getInfoStepData,
  getOnboardingFields,
  getPreconfirmationStepFieldsByName,
  getSupportMail,
  MailLink,
  parseStateAddress,
  WhatsappLink,
  withPhonePlus,
} from "../../utils";
import { logAnalyticsAndHotjarEvent } from "../../main";
import InteractiveInformation from "../../components/InteractiveInformation/InteractiveInformation";
import { showToastMessage } from "@allaria-tech/matter/ToastMessage";
import { onUpdateInfoStep } from "../../reducers/infoStepReducer";
import { doc } from "firebase/firestore";
import onboardingJuridical from "./juridical";
import { RootState, store } from "../../app/store";
import { AppDispatch } from "../../app/store";
import { getResumeStepData } from "../../components/TemplateInterpreter/utils";
import {
  updateDraftBase,
  updateSignatureRequestId,
  updateToken,
} from "../../reducers/authReducer";
import stylesSupplementaryComponentInfoRenderer from "../../components/SupplementaryComponentInfoRenderer/SupplementaryComponentInfoRenderer.module.scss";
import { documentTypesParsed, firestoreCollections } from "../../constants";
import PreloadTemplateLanding from "../../components/PreloadTemplateLanding/PreloadTemplateLanding";
import { getCompanyData } from "../../utils/companies";
import { fieldsFrom, pepFields, spouseFields } from "../../inputs";
import { Icon } from "@allaria-tech/matter/Icon";

const getActivities = async (): Promise<PitbullActivities> => {
  try {
    const { data } = await getPhysicalActivities();
    return data;
  } catch (error) {
    throw new Error("Error al obtener las actividades");
  }
};

const getCountries = async (): Promise<PitbullCountries> => {
  try {
    const { data } = await getCountryList();
    return data;
  } catch (error) {
    throw new Error("Error al obtener los países");
  }
};

const getValidCuit = (cuit: string, state: RootState) => {
  const renaperCuil = state?.renaper?.cuil;
  if (renaperCuil && /^(20|23|24|27|30|33)[0-9]{9}$/.test(renaperCuil)) {
    return renaperCuil;
  }
  return cuit || "";
};

const stepOne: ComponentStep = {
  step: 1,
  name: "Send",
  hideStepper: true,
  component: (dispatch, state, formik) => {
    if (!state || !dispatch || !formik)
      throw new Error("Error: state, dispatch or formik not found");

    return <PreloadTemplateLanding />;
  },
  disableBackNavigation: true,
};

const stepTwo: ComponentStep = {
  step: 2,
  name: "Mail",
  hideStepper: true,
  component: (dispatch, state, formik) => {
    if (!state || !dispatch || !formik)
      throw new Error("Error: state, dispatch or formik not found");

    const completeSteps = (representative: Representative) => {
      const {
        representativeInformation: {
          cuit,
          gender,
          pep,
          pepDetails,
          telephone,
          identityDocument,
          addresses,
          spouse,
          birthDate,
          ...rest
        },
      } = representative;

      const parsedPep = pep ?? "NONE";
      const parsePepDetails =
        parsedPep && parsedPep !== "NONE" ? pepDetails : undefined;
      const parsedGender =
        gender === "MALE" ? "M" : gender === "FEMALE" ? "F" : "X";

      const spouseData = {
        spouseName: spouse?.name,
        spouseLastName: spouse?.lastName,
        spouseCuil: spouse?.cuit,
      };

      //Parse birthDate
      let birthDateParsed = "";
      if (birthDate) {
        birthDateParsed = parseBirthDate(birthDate);
      }

      const addressesData = {
        country: addresses[0].country,
        fullAddress: buildFullAddress(
          addresses[0].street,
          addresses[0].number,
          addresses[0].city,
        ),
        zipCode: addresses[0].zipCode,
        state: addresses[0].state,
        municipality: addresses[0].municipality,
        city: addresses[0].city,
        street: addresses[0].street,
        number: addresses[0].number,
        floor: addresses[0].floor,
        apartment: addresses[0].apartment,
      };

      const representativeParsed = {
        cuit,
        birthDate: birthDateParsed,
        gender: parsedGender,
        pep: parsedPep,
        pepDetails: parsePepDetails,
        telephone: parseTelephone(telephone),
        identityDocument: identityDocument?.idNumber || getDniFromCuit(cuit),
        tramitNumber: identityDocument?.procedureId,
        version: identityDocument.version,
        expirationDate: identityDocument.expirationDate,
        ...(addresses && addresses.length > 0 && { addresses: addressesData }),
        ...(spouse && spouseData),
        ...rest,
      };

      formik?.setValues(
        (prevValues) => ({
          ...prevValues,
          ...representativeParsed,
        }),
        false,
      );
      dispatch(onUpdateFormData(representativeParsed));
    };

    const onAuthorize = async (draftUUID?: string) => {
      try {
        const params = new URLSearchParams(document.location.search);
        let email = params.get("email");

        if (email) {
          email = decodeURIComponent(email).replace(/ /g, "+");
        }

        if (!draftUUID || !state || !formik || !email)
          throw new Error("DraftUUID or state not found");

        try {
          const draftUUID = state.onboardingData.draftUUID;
          const draftResponse = await getDraft({
            draftUUID,
          });

          const onboardingSteps = draftResponse.data.steps;

          dispatch(
            onUpdateFormData({
              steps: onboardingSteps,
            }),
          );

          // For preconfirmation
          formik?.setValues(
            (prevValues) => ({
              ...prevValues,
              steps: onboardingSteps,
            }),
            false,
          );

          const businessDocumentation = await getBusinessDocuments({
            draftUUID,
          });

          const businessDocumentsByType = businessDocumentation.data
            .filter(
              ({ type }) => !["SELFIE", "DNI_BACK", "DNI_FRONT"].includes(type),
            )
            .reduce(
              (acc, doc) => {
                const type = doc.type as DocumentBusinessType;
                if (!acc[type]) {
                  acc[type] = [];
                }
                acc[type].push(doc);
                return acc;
              },
              {} as Record<string, DocumentMetadata[]>,
            );

          if (businessDocumentation) {
            dispatch(
              onUpdateFormData({
                businessDocumentation: businessDocumentsByType,
              }),
            );
          }

          const additionalSignatory = onboardingSteps
            .find((step: StepThreePJ) => step["@type"] === "3_JURIDICAL")
            .additionalSignatories.find(
              (signatory: Signatory) => signatory && signatory.email === email,
            );

          if (additionalSignatory) {
            const businessInformation = onboardingSteps.find(
              (step: StepTwoPJ) => step["@type"] === "2_JURIDICAL",
            ).businessInformation;

            const { data } = await getSignatoryDraft({
              cuit: businessInformation.cuit,
            });

            if (data.representativeInformation.cuit) {
              completeSteps(data);
              dispatch(onUpdateInfoStep("ACTIVE_ACCOUNT"));
              dispatch(onUpdateHasExternalInformation(true));
            }
          }
        } catch (error) {
          console.log("Error on getDraft", error);
        }
      } catch (error) {
        console.log(error, "Error on authorize");
        Sentry.captureException(error);
      }
    };

    const onSuccess = async () => {
      const { type } = store.getState().infoStep;
      if (type !== null) {
        dispatch(onUpdateStep("information-step"));
      } else {
        dispatch(onUpdateCompletedStep(2));
        dispatch(onUpdateReachedStep(3));
        dispatch(onUpdateStep(3));
      }
    };

    return (
      <TokenStep
        onSuccess={onSuccess}
        onAuthorize={onAuthorize}
        formik={formik}
        oldAuth
      />
    );
  },
  disableButtonBack: true,
  disableBackNavigation: true,
  supplementaryComponentInfo: {
    show: (state) => {
      const token = state?.authentication?.token;
      return !!(token && !isTokenActive({ token }));
    },
    position: "top",
    component: () => {
      return (
        <p className={stylesSupplementaryComponentInfoRenderer.emailValidation}>
          Validá tu dirección de mail para retomar el proceso de registro
        </p>
      );
    },
  },
};

const stepThree = (
  countries: PitbullCountry[],
  countriesOptions: Option[],
  activitiesOptions: Option[],
  getProvincesFilteredByCountry: (country?: string) => PitbullProvinces,
  provinces: PitbullProvinces,
): Step => {
  const provincesOption = provinces.map((prov) => ({
    value: prov.name,
    label: prov.name,
  }));

  const uploadDniCard = (
    dispatch: AppDispatch,
    state: RootState | undefined,
    formik: FormikProps<FormikValues> | undefined,
    currentDniStep?: DniStep,
  ) => (
    <UploadDniCard
      dispatch={dispatch}
      state={state}
      formik={formik}
      currentDniStep={currentDniStep}
      getDniRef={({ db, uuid, email }) =>
        doc(db, firestoreCollections.dniSignatory, `${uuid}-${email}`)
      }
      onUploadDni={async (type, file, uuid, cuit) => {
        await sendBusinessRepresentativeDocument({
          draftUUID: uuid,
          documentType: documentTypesParsed[type] as DocumentRepresentativeType,
          file,
          cuit: cuit,
        });
      }}
    />
  );

  return {
    step: 3,
    name: "Datos personales",
    hideStepper: true,
    subSteps: [
      {
        step: 1,
        disableBackNavigation: true,
        disableButtonBack: true,
        fields: [
          {
            name: "cuit",
            type: "cuit",
            label: "CUIT/CUIL",
            initialValue: "",
            placeholder: "XX-XXXXXXXX-X",
            conditionalValidation: (state) => {
              const representativeCuit =
                state?.onboardingData?.formValues?.steps?.[0]
                  ?.representativeInformation?.cuit;

              if (representativeCuit) {
                return cuitPFRequired.notOneOf(
                  [representativeCuit],
                  "El CUIT no puede ser el mismo que el del representante.",
                );
              }
              return cuitPFRequired;
            },
            formatInPreConfirmation: (value) => (value ? asCuit(value) : "-"),
          },
        ],
      },
      {
        step: 2,
        label: "uploadDNI", // Used to identify the step in remoteSteps
        component: (dispatch, state, formik) =>
          uploadDniCard(dispatch, state, formik),
        insideForm: true,
        onContinue: async (values, dispatch, state) => {
          if (!state || !dispatch) return;

          const { completedRemote } = state.onboardingData;
          if (!completedRemote) return;
          if (!hasRenaperData(state)) return;
          const informationParsed = state.renaper;

          await checkIsEmailAvailable({
            dispatch,
            idNumber: values.identityDocument || getDniFromCuit(values.cuit),
          });

          const body = getParsedRenaperData(
            values,
            state,
            informationParsed,
            countries,
          );
          dispatch(onUpdateFormData({ ...body }));

          return {
            newValuesToForm: body,
          };
        },
      },
      {
        step: 3,
        subtitle: "Cargá fotos del frente de tu DNI",
        component: (dispatch, state, formik) =>
          uploadDniCard(dispatch, state, formik, "upload_front"),
        skipWhen: (_, state) => state.onboardingData.completedRemote,
        insideForm: true,
      },
      {
        step: 4,
        subtitle: "Cargá fotos del dorso de tu DNI",
        component: (dispatch, state, formik) =>
          uploadDniCard(dispatch, state, formik, "upload_back"),
        skipWhen: (_, state) => state.onboardingData.completedRemote,
        onContinue: async (values, dispatch, state) => {
          if (!state || !dispatch) return;

          if (!hasRenaperData(state)) return;
          const informationParsed = state.renaper;

          await checkIsEmailAvailable({
            dispatch,
            idNumber: values.identityDocument || getDniFromCuit(values.cuit),
          });

          const body = getParsedRenaperData(
            values,
            state,
            informationParsed,
            countries,
          );
          dispatch(onUpdateFormData({ ...body }));

          return {
            newValuesToForm: body,
          };
        },
        insideForm: true,
      },
      {
        step: 5,
        fields: fieldsFrom(
          [
            "identityDocument",
            {
              disabled: true,
              tooltip: {
                label: <Icon name="edit" />,
                modal: "modal-edit-dni",
                external: true,
              },
            },
          ],
          "tramitNumber",
          "gender",
        ),
        skipWhen: (_, state) => hasRenaperData(state),
        onContinue: async (values: FormikValues, dispatch, state) => {
          if (!dispatch || !values || !state) return;

          return await onContinueRenaper(values, dispatch, state, countries);
        },
        disableBackNavigation: true,
        disableButtonBack: true,
      },
      {
        step: 6,
        label: "livenessValidation", // Used to identify the step in remoteSteps
        titleOfStep: "Validá tu identidad",
        hideFormHeader: hideLivenessStepHeader,
        component: (dispatch, state, formik) => {
          return (
            <LivenessValidationCard
              dispatch={dispatch}
              state={state}
              formik={formik}
              getLivenessRef={({ db, uuid, email }) =>
                doc(
                  db,
                  firestoreCollections.livenessSignatory,
                  `${uuid}-${email}`,
                )
              }
            />
          );
        },
        onBack: async (_, __, state) => {
          if (!state) return;

          const showCompleteDniDataStep = !hasRenaperData(state);

          return {
            backSubStep: showCompleteDniDataStep ? 5 : 4,
          };
        },
        insideForm: true,
      },
      {
        step: 7,
        disableButtonBack: true,
        disableBackNavigation: true,
        fields: fieldsFrom(
          ["name", { disabled: true }],
          ["lastName", { disabled: true }],
          [
            "citizenship",
            {
              name: "citizenship",
              type: "text",
              label: "Nacionalidad",
              validation: smallStringRequired,
              initialValue: "",
              columns: 2,
              formatInPreConfirmation: (value) => capitalizeString(value),
              onChange: (
                _values,
                setFieldValue,
                _,
                _setFieldTouched,
                value,
              ) => {
                setFieldValue("citizenship", value);
              },
            },
          ],
          "birthDate",
          "civilStatus",
          ...spouseFields(),
          "telephone",
          [
            "activities",
            {
              validation: multipleSelectRequired(activitiesOptions),
              options: activitiesOptions,
            },
          ],
          ...pepFields(),
        ),
      },
      {
        step: 8,
        fields: [
          {
            name: "addresses.country",
            type: "select",
            label: "País de residencia",
            placeholder: "Seleccioná una opción",
            validation: radioOrSingleSelectRequired(countriesOptions),
            options: countriesOptions,
            selectType: "single",
            initialValue: "",
            onChange: (_, setFieldValue) => {
              const fieldsToClear = {
                "addresses.fullAddress": "",
                "addresses.municipality": "",
                "addresses.stateSelect": "",
                "addresses.stateText": "",
                "addresses.state": "",
                "addresses.zipCode": "",
                "addresses.floor": "",
                "addresses.apartment": "",
              };

              Object.entries(fieldsToClear).forEach(([field, value]) => {
                setFieldValue(field, value);
              });
            },
          },
          {
            name: "addresses.fullAddress",
            type: "address",
            label: "Domicilio",
            validation: mediumSelectedAddressRequired,
            initialValue: "",
            autoSelect: "addresses.fullAddress",
            country: "addresses.country",
            onChange: (values, setFieldValue) => {
              const {
                formattedAddress: {
                  postalCode: zipCode,
                  state,
                  city,
                  municipality,
                  street,
                  streetNumber: number,
                  country,
                },
              } = values;

              const provinces = getProvincesFilteredByCountry(country);

              const fusedProvince = fuseProvince(provinces, state, city);

              //// If no records are found in FUSE, we set the first province by default to avoid breaking
              const finalProvince = fusedProvince || provinces[0].name;

              setFieldValue("addresses.zipCode", zipCode);
              setFieldValue("addresses.state", finalProvince);
              setFieldValue("addresses.city", city);
              setFieldValue("addresses.municipality", municipality);
              setFieldValue("addresses.street", street);
              setFieldValue("addresses.number", number);

              const newFullAddress = buildFullAddress(
                street,
                number,
                city,
                municipality,
              );

              setFieldValue("addresses.fullAddress", newFullAddress);
            },
            autoComplete: "off",
            formatInPreConfirmation: (value) =>
              value?.split(",")?.[0]?.replace("null", "") ?? "",
          },
          {
            name: "addresses.floor",
            type: "text",
            label: "Piso",
            validation: smallString,
            initialValue: "",
            columns: 3,
            maxLength: 10,
          },
          {
            name: "addresses.apartment",
            type: "text",
            label: "Departamento",
            validation: smallString,
            initialValue: "",
            columns: 3,
            maxLength: 10,
          },
          {
            name: "addresses.zipCode",
            type: "text",
            label: "Código postal",
            validation: zipCodeRequired,
            initialValue: "",
            columns: 3,
          },
          {
            name: "addresses.municipality",
            type: "text",
            label: "Localidad",
            validation: smallStringRequired,
            initialValue: "",
            columns: 2,
          },
          {
            name: "addresses.state",
            type: "select",
            label: "Provincia",
            validation: radioOrSingleSelectRequired(provincesOption),
            initialValue: "",
            searchable: true,
            dynamicOptions: (formik) => {
              const value = formik.values.addresses?.country || "Argentina";

              //Filter provinces by country
              const provinces = getProvincesFilteredByCountry(
                value || undefined,
              );

              const provincesOption = provinces.map((prov) => ({
                value: prov.name,
                label: prov.name,
              }));

              return provincesOption;
            },
            columns: 2,
          },
        ],
      },
    ],
    disableBackNavigation: true,
    disableButtonBack: true,
  };
};

const infoStep: Step = {
  step: "information-step",
  component: (dispatch, state, formik) => {
    if (!state || !dispatch || !formik)
      throw new Error("Error: state, dispatch or formik not found");
    const email = formik?.values?.email;
    const cuit = formik?.values?.cuit;
    const identityDocument = formik?.values?.identityDocument;
    //Set user in Sentry
    if (email) {
      Sentry.setUser({ email });
    }

    const { draftUUID, stepData } = state.onboardingData;
    const { type } = state.infoStep;
    const { token } = state.authentication;

    const isBlacklistStep = type === "BLACKLIST";
    const isActiveAccountStep = type === "ACTIVE_ACCOUNT";
    const isPendingRegistryStep = type === "PENDING_REGISTRY_LOCAL";

    const checkToken = async () => {
      if (token) {
        if (!isTokenActive({ token })) {
          dispatch({ type: "reset-store" });
          dispatch(onUpdateFormData({ email, cuit }));
          dispatch(onUpdateDraftUUID(draftUUID));
          dispatch(onUpdateCompletedStep(1));
          dispatch(updateToken(token));

          formik?.resetForm({
            values: { email, cuit, businessInformation: { cuit } },
          });
          const res = await authAdditionalSignerEmail({
            email,
            companyType: getCompanyData().companyName,
          });
          dispatch(updateSignatureRequestId(res.data.signatureRequestId));
          dispatch(updateDraftBase(null));
          dispatch(onUpdateStep(2));
        }
      }
    };

    // Continue
    const onPrimaryClick = async () => {
      try {
        checkToken();
        if (isBlacklistStep) {
          contactBlacklistSupport();
        } else if (isActiveAccountStep) {
          dispatch(onUpdateStep(3));
          dispatch(onUpdateReachedStep(3));
          dispatch(onUpdateCompletedStep(2));
        } else if (isPendingRegistryStep) {
          if (identityDocument) {
            await checkIsEmailAvailable({
              dispatch,
              idNumber: identityDocument,
            });
          }

          const latestState = store.getState();
          const { step: nextStep, substep: nextSubStep } = getResumeStepData({
            steps: latestState.activeTemplate?.steps,
            stepData,
            values: formik.values,
            state: latestState,
          });

          dispatch(onUpdateStep(nextStep));
          dispatch(onUpdateSubStep(nextSubStep));
          logAnalyticsAndHotjarEvent("onboarding_resumed");
        } else if (type === "REGISTERED_EMAIL") {
          dispatch(onUpdateStep(3));
          dispatch(onUpdateSubStep(1));
          dispatch(onUpdateReachedStep(3));
          dispatch(onUpdateCompletedStep(2));
        }
      } catch (error) {
        console.log(error);
        showToastMessage(genericToastifyError, {
          containerId: "informationAlert",
          type: "ERROR",
        });
      }
    };

    // Restart or back
    const onSecondaryClick = async () => {
      try {
        checkToken();
        if (isBlacklistStep) {
          dispatch(onUpdateStep(3));
        }
        if (isPendingRegistryStep) {
          const { email } = formik.values;

          const uuid = `${draftUUID}-${email}`;
          await deleteDniImages({
            collection: firestoreCollections.dniSignatory,
            uuid,
          });
          await deleteLiveness({
            collection: firestoreCollections.livenessSignatory,
            uuid,
          });

          dispatch({ type: "reset-store" });

          if (type === "PENDING_REGISTRY_LOCAL") {
            formik.resetForm();
            dispatch(onUpdateStep(1));
            dispatch(onUpdateReachedStep(1));
          }
          logAnalyticsAndHotjarEvent("onboarding_signatory_restart");
        }
      } catch (error) {
        showToastMessage(genericToastifyError, {
          containerId: "informationAlert",
          type: "ERROR",
        });
      }
    };

    const { alertType, title, subtitle, primaryText, secondaryText } =
      getInfoStepData({ infoStep: type, onboardingType: "SIGNATORY" });

    const primarybutton = {
      text: primaryText,
      onClick: onPrimaryClick,
    };
    const secondarybutton = {
      text: secondaryText,
      onClick: onSecondaryClick,
    };

    return (
      <InteractiveInformation
        alertType={alertType}
        title={title}
        subtitle={subtitle}
        primaryButton={primarybutton}
        secondaryButton={secondarybutton}
      />
    );
  },
  hideLayoutButtons: true,
  hideStepper: true,
  centeredLogo: true,
  supplementaryComponentInfo: {
    show: (state) => {
      const { type } = state.infoStep;
      return type === "REGISTERED_EMAIL";
    },
    position: "bottom",
    component: () => {
      const { helpTextContainer } = stylesSupplementaryComponentInfoRenderer;
      return (
        <div className={helpTextContainer}>
          <p>
            Por cualquier duda podés comunicarte con nosotros enviándonos <br />{" "}
            un mail a <MailLink> {getSupportMail()}</MailLink> o via {""}
            <WhatsappLink>Whatsapp</WhatsappLink>
          </p>
        </div>
      );
    },
  },
};

const onboardingSignatory = async (
  dispatch: AppDispatch,
): Promise<Template> => {
  // const params = new URLSearchParams(document.location.search);
  // const externalCode = params.get("externalCode") || params.get("uuid");

  try {
    // if (!externalCode) throw new Error("External code not found");

    const onboardingJuridicalTemplate = await onboardingJuridical(
      undefined, // If no draftUUID is passed, no documents request will be executed
      dispatch,
    );

    const [activities, countries] = await Promise.all([
      getActivities(),
      getCountries(),
    ]);

    const activitiesOptions: Option[] = (
      activities as PitbullActivityPhysical[]
    )?.map(({ name }) => {
      return { value: name, label: name };
    });

    const countriesOptions: Option[] = countries?.map(({ name }) => {
      return { value: name, label: name };
    });

    const countriesTyped = countries as PitbullCountry[];

    const provinces = await getProvincesByIso();

    const getCountryIsoAlpha2 = (country: string) => {
      return countriesTyped.find(
        (countryTyped) => countryTyped.name === country,
      )?.isoAlpha2;
    };

    const getProvincesFilteredByCountry = (country = "Argentina") => {
      const iso = getCountryIsoAlpha2(country);
      const filteredProvince = provinces.filter((prov) => {
        if (prov.countryIsoAlpha2 === iso) {
          return prov;
        }
      });
      return filteredProvince;
    };

    const { favicon, formTitle, logo } = getCompanyData();

    const onboardingSteps = [
      stepOne,
      stepTwo,
      stepThree(
        countries,
        countriesOptions,
        activitiesOptions,
        getProvincesFilteredByCountry,
        provinces,
      ),
      infoStep,
    ];

    const businessPreconfirmationStep = ({
      values,
    }: {
      values: FormikValues;
    }): PreconfirmationStep => {
      let businessPreconfirmationFields: PreconfirmationField[] = [];
      const stepTwoPJ: StepTwoPJ = values.steps.find(
        (step: StepTwoPJ) => step["@type"] === "2_JURIDICAL",
      );

      const businessInformationStep = onboardingJuridicalTemplate?.steps?.find(
        (step) => step.step === 5,
      );

      if (businessInformationStep) {
        const businessInformationFields = getOnboardingFields([
          businessInformationStep,
        ]);

        const businessInformationFieldsNames =
          getPreconfirmationStepFieldsByName([
            {
              initialValue: "",
              type: "cuit",
              name: "businessInformation.cuit",
              label: "CUIT de la empresa",
              validation: cuitRequired,
              preconfirmationLabel: "CUIT/CUIL",
              formatInPreConfirmation: (value) => asCuit(value),
            },
            ...businessInformationFields,
          ]);

        const businessInformationFieldsValues = getBusinessInformationValues(
          stepTwoPJ.businessInformation,
        );

        const fieldsNamesInBusinessPreconfirmationStep = [
          "businessInformation.legalName",
          "businessInformation.cuit",
          "entityAddress.fullAddress",
          "entityAddress.floor",
          "entityAddress.apartment",
          "entityAddress.zipCode",
          "entityAddress.municipality",
          "entityAddress.state",
          "businessInformation.corporateType",
          "businessInformation.activities",
          "businessInformation.registrationCountry",
          "businessInformation.registrationProvince",
          "businessInformation.registrationDate",
          "businessInformation.registrationNumber",
          "businessInformation.registrationAuthority",
          "businessInformation.isPublicEntity",
          "businessInformation.isInEconomicGroup",
          "businessInformation.bankAccounts",
        ];

        businessPreconfirmationFields = [
          ...fieldsNamesInBusinessPreconfirmationStep.map((field) => {
            return {
              ...businessInformationFieldsNames[field],
              values: businessInformationFieldsValues,
              valueGetter: (values: FormikValues) => values[field],
            };
          }),
        ];
      }

      return {
        name: "Datos de la entidad",
        columns: 4,
        fields: businessPreconfirmationFields,
      };
    };

    const getBusinessInformationValues = (
      businessInformation: StepTwoPJ["businessInformation"],
    ) => {
      const {
        legalName,
        cuit,
        addresses,
        corporateType,
        activities,
        registrationCountry,
        registrationProvince,
        registrationDate,
        registrationNumber,
        registrationAuthority,
        isPublicEntity,
        isInEconomicGroup,
        bankAccounts,
      } = businessInformation;

      const address = addresses[0];

      return {
        "businessInformation.legalName": legalName,
        "businessInformation.cuit": cuit,
        "entityAddress.fullAddress": `${address.street} ${address.number}`,
        "entityAddress.floor": address.floor ?? "-",
        "entityAddress.apartment": address.apartment ?? "-",
        "entityAddress.zipCode": address.zipCode,
        "entityAddress.municipality": address.municipality,
        "entityAddress.city": address.city,
        "entityAddress.street": address.street,
        "entityAddress.state": address.state,
        "businessInformation.corporateType": corporateType,
        "businessInformation.activities": activities,
        "businessInformation.registrationCountry": registrationCountry,
        "businessInformation.registrationProvince": registrationProvince,
        "businessInformation.registrationDate":
          parseBirthDate(registrationDate),
        "businessInformation.registrationNumber": registrationNumber,
        "businessInformation.registrationAuthority": parseRegistrationAuthority(
          registrationAuthority,
        ),
        "businessInformation.isPublicEntity": isPublicEntity ? "Sí" : "No",
        "businessInformation.isInEconomicGroup": isInEconomicGroup
          ? "Sí"
          : "No",
        "businessInformation.bankAccounts":
          bankAccounts && bankAccounts?.length > 0 ? "Sí" : "No",
      };
    };

    const documentsPreconfirmationStep = ({
      values,
    }: {
      values: FormikValues;
    }): PreconfirmationStep => {
      const stepTwoPJ: StepTwoPJ = values.steps.find(
        (step: StepTwoPJ) => step["@type"] === "2_JURIDICAL",
      );
      const corporateType = stepTwoPJ?.businessInformation?.corporateType;
      const businessDocumentsTypes = getBusinessDocumentsByCorporateType(
        translateCorporateReversed[corporateType],
      );

      const documentsPreconfirmationFields: PreconfirmationField[] =
        businessDocumentsTypes.map((type) => {
          return {
            name: `businessDocumentation.${type}`,
            label: getDocumentTypeParsed(
              translateCorporateReversed[corporateType],
              type as DocumentBusinessType,
            ),
          };
        });

      return {
        name: "Documentación",
        columns: 2,
        fields: documentsPreconfirmationFields,
        isDocumentStep: true,
      };
    };

    const onboardingFields = getOnboardingFields(onboardingSteps);
    const formattedOnboardingFields =
      getPreconfirmationStepFieldsByName(onboardingFields);

    const formattedOnboardingFieldsNames = Object.keys(
      formattedOnboardingFields,
    );

    const signatoryPreconfirmationStep: PreconfirmationStep = {
      name: "Datos del firmante",
      columns: 4,
      fields: [
        ...formattedOnboardingFieldsNames.map(
          (field) => formattedOnboardingFields[field],
        ),
      ],
      editStep: {
        step: 3,
        substep: 7,
      },
    };

    const signatoriesPreconfirmationStep = ({
      values,
    }: {
      values: FormikValues;
    }): PreconfirmationStep => {
      // There are cases where the original signer incorrectly enters some information
      // of the additional signer. So instead of get from 'additionalSignatories',
      // we read from the correct values of the current draft.
      const currentSignatoryEmail = values?.email || "";

      const currentSignatory = [
        {
          email: currentSignatoryEmail,
          name: values?.name,
          lastName: values?.lastName,
          idNumber: values?.identityDocument,
        },
      ];
      const originalSignatory =
        values?.steps?.[0].representativeInformation || {};

      const additionalSignatories =
        values?.steps?.[2].additionalSignatories || [];

      const additionalSignatoriesWithoutActualSignatory =
        additionalSignatories.filter((signatory: Signatory) => {
          return signatory?.email !== currentSignatoryEmail;
        });

      const getAdditionalSignatory = () => {
        const completedRepresentatives = values?.steps?.[6]?.representatives;

        // If no completed representatives, return the existing additional signatories
        if (completedRepresentatives?.length == 0) {
          return additionalSignatoriesWithoutActualSignatory;
        }

        // Find the representative that matches an additional signatory
        const matchedRepresentative = completedRepresentatives.find(
          (representative: Representative) =>
            additionalSignatoriesWithoutActualSignatory.some(
              (signatory: SignatoryData) =>
                signatory.email ===
                representative?.representativeInformation?.email,
            ),
        );

        // If we found a match, return formatted representative data
        if (matchedRepresentative) {
          const { representativeInformation: info } = matchedRepresentative;
          return [
            {
              email: info.email,
              name: info.name,
              lastName: info.lastName,
              idNumber: info.identityDocument.idNumber,
            },
          ];
        }

        // No matches found, return existing additional signatories
        return additionalSignatoriesWithoutActualSignatory;
      };

      const signatories = [
        {
          email: originalSignatory.email,
          name: originalSignatory.name,
          lastName: capitalizeString(originalSignatory.lastName),
          idNumber: originalSignatory.identityDocument.idNumber,
        },
        ...currentSignatory,
        ...getAdditionalSignatory(),
      ];

      const signatoriesPreconfirmationFields = Array.from(
        { length: signatories.length },
        (_, i) => {
          const signatoryFields: Field[] = [
            {
              name: "fullName",
              type: "text",
              label: `Firmante ${i + 1}`,
              validation: smallStringRequired,
              initialValue: "",
              columns: 2,
              valueGetter: (values) =>
                `${values.name} ${capitalizeString(values.lastName)} ${
                  currentSignatoryEmail === values.email ? "(Tú)" : ""
                }`,
            },
            {
              name: "idNumber",
              type: "dni",
              label: "DNI",
              initialValue: "",
              formatInPreConfirmation: (value) => (value ? asDni(value) : ""),
            },
            {
              type: "email",
              name: "email",
              label: "Mail",
              initialValue: "",
            },
          ];

          return getFormattedPreconfirmationFields(
            signatoryFields,
            signatories[i],
          );
        },
      );

      return {
        name: `Firmantes (${signatories.length})`,
        columns: 3,
        fieldsWithSeparator: signatoriesPreconfirmationFields,
      };
    };

    const getPreconfirmationSteps = ({
      values,
    }: {
      values?: FormikValues | null;
    }): PreconfirmationStep[] => {
      if (!values) throw new Error("Error: values not found");

      return [
        businessPreconfirmationStep({ values }),
        signatoryPreconfirmationStep,
        documentsPreconfirmationStep({ values }),
        signatoriesPreconfirmationStep({ values }),
      ];
    };

    const getPreconfirmationTerms = ({
      values,
    }: {
      values?: FormikValues | null;
    }) => {
      if (!values) throw new Error("Error: values not found");

      const stepSix: StepSixPJ = values.steps.find(
        (step: StepSixPJ) => step["@type"] === "6_JURIDICAL",
      );
      const acceptedOptionalTermsByRepresentative =
        stepSix.acceptedOptionalTermsAndConditions;

      const acceptedWantsAllariaAccount = stepSix.wantsAllariaAccount;

      // The signatory's own form has no referrer: the partnership is the company's.
      const representativeStep: StepOnePJ | undefined = values.steps.find(
        (step: StepOnePJ) => step["@type"] === "1_JURIDICAL",
      );
      const onboardingTerms = getCompanyData({
        referrer: representativeStep?.referrer,
      }).termsAndConditions;
      const signatoryTerms = onboardingTerms.filter(
        ({ required, name }) =>
          required ||
          (name === "senebiAndExteriorNotes" &&
            acceptedOptionalTermsByRepresentative) ||
          (name === "wantsAllariaAccount" && acceptedWantsAllariaAccount) ||
          (name === "acceptedFalTermsAndConditions" &&
            acceptedOptionalTermsByRepresentative),
      );

      return signatoryTerms.map((term) => ({
        ...term,
        initialValue: false,
        required: true,
        validation: checkboxRequired,
      }));
    };

    const handleSendRepresentative = async (state: RootState) => {
      const formValues = state.onboardingData.formValues;

      if (!formValues) throw new Error("Error: formValues not found");

      const onboardingSteps = formValues?.steps;
      const representativeStep: StepOnePJ = onboardingSteps.find(
        (step: StepOnePJ) => step["@type"] === "1_JURIDICAL",
      );

      const representativeEmail =
        representativeStep.representativeInformation.email || "";

      const shareholdersStep: StepFourPJ = onboardingSteps.find(
        (step: StepFourPJ) => step["@type"] === "4_JURIDICAL",
      );

      const newShareholdersStep = {
        ...shareholdersStep,
        shareholders: shareholdersStep.shareholders.map((shareholder) => {
          return {
            "@type": shareholder.personType,
            ...shareholder,
          };
        }),
      };

      const steps = getDraftStepsToSend(onboardingSteps, newShareholdersStep);

      try {
        const {
          name,
          lastName,
          gender,
          identityDocument,
          tramitNumber,
          citizenship,
          civilStatus,
          telephone,
          birthDate,
          birthCountry,
          pep,
          addresses: {
            floor,
            apartment,
            state: stateAddress,
            city,
            street,
            number,
            municipality,
            country,
            zipCode,
          },
          spouseCuil,
          spouseLastName,
          spouseName,
          activities,
          cuit,
        } = formValues;

        const email = state?.onboardingData?.formValues?.email;

        const stepRepresentatives = onboardingSteps.find(
          (step: Representative) => step["@type"] === "7_JURIDICAL",
        ).representatives;

        const identityDocumentRenaper = stepRepresentatives?.find(
          (rep: Representative) =>
            rep.representativeInformation.email === email,
        )?.representativeInformation.identityDocument;

        const versionOfIdentity =
          state?.renaper?.copy || identityDocumentRenaper?.version;

        const creationDate = state?.renaper?.creationDate
          ? parseDate(state.renaper.creationDate)
          : identityDocumentRenaper?.creationDate;

        const expirationDate = state?.renaper?.expirationDate
          ? parseDate(state.renaper.expirationDate)
          : identityDocumentRenaper?.expirationDate;

        const procedureId = state.renaper.procedureId || tramitNumber;

        const stepSeven = {
          "@type": "7_JURIDICAL",
          representatives: [
            {
              "@type": "1_JURIDICAL",
              referrer: "",
              representativeInformation: {
                activities,
                name,
                lastName,
                cuit: getValidCuit(cuit, state),
                email,
                gender: parseGenderFromInitial[gender as GenderInitials],
                identityDocument: {
                  type: "DNI" as IdentityType,
                  idNumber: identityDocument,
                  procedureId,
                  version: versionOfIdentity as IdentityVersion,
                  creationDate,
                  expirationDate,
                },
                telephone: withPhonePlus(telephone),
                citizenship,
                birthCountry,
                birthDate: parseDate(birthDate),
                civilStatus,
                pep,
                pepDetails: pep === "NONE" ? undefined : formValues.pepDetails,
                addresses: [
                  {
                    street,
                    number,
                    zipCode,
                    city,
                    state: parseStateAddress(stateAddress) || "",
                    country,
                    floor,
                    apartment,
                    municipality,
                  },
                ],
                spouse:
                  civilStatus === "MARRIED"
                    ? {
                        ...(spouseCuil && { cuit: spouseCuil }),
                        lastName: spouseLastName,
                        name: spouseName,
                      }
                    : undefined,
              },
            },
          ],
        };

        const body = {
          companyType: getCompanyData().companyName,
          personType: "JURIDICAL" as PersonType,
          email: representativeEmail,
          steps: getDraftStepsToSend(steps, stepSeven),
        };

        await sendStep({ stepId: 7, body });
      } catch (error) {
        console.log("Error step seven: ", error);
        throw new Error("Error al enviar los datos del representante");
      }
    };

    return {
      onboardingType: "SIGNATORY",
      formTitle,
      logo,
      icon: bodyIcon,
      favicon,
      steps: onboardingSteps,
      remoteSteps: [
        {
          link: "/dni",
          step: stepThree(
            countries,
            countriesOptions,
            activitiesOptions,
            getProvincesFilteredByCountry,
            provinces,
          ).subSteps?.find(
            ({ label }) => label === "uploadDNI",
          ) as ComponentStep,
        },
        {
          link: "/identidad",
          step: stepThree(
            countries,
            countriesOptions,
            activitiesOptions,
            getProvincesFilteredByCountry,
            provinces,
          ).subSteps?.find(
            ({ label }) => label === "livenessValidation",
          ) as ComponentStep,
        },
      ],
      preconfirmation: ({ values }) => ({
        steps: getPreconfirmationSteps({ values }),
        checkboxes: getPreconfirmationTerms({ values }),
        sendTermsAndConditions: handleSendRepresentative,
      }),
    };
  } catch (error) {
    throw new Error(error as string);
  }
};
export default onboardingSignatory;
